import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { extractDefaultStoreId, HxCurrencyPipe, HxPaymentApprovalService, isoDate, PaymentApprovalModel, PaymentApprovalRequest, StorageInvoiceModel } from 'hx-services';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgFor, NgIf } from '@angular/common';
import { HxBankAccountSelectComponent, HxDateSelectComponent, HxInvoiceSelectComponent, HxStoreSelectComponent } from 'hx-component';
import { SuppliersSearchComponent } from '../../../components/suppliers-search/suppliers-search.component';

interface StorageInvoiceInfoResponseDto extends StorageInvoiceModel {
  debtTotal?: number;
}

@Component({
    selector: 'app-payment-approval-edit',
    templateUrl: './payment-approval-edit.component.html',
    styleUrls: ['./payment-approval-edit.component.css'],
    encapsulation: ViewEncapsulation.None,
    imports: [NgIf, FormsModule, ReactiveFormsModule, HxStoreSelectComponent, HxDateSelectComponent, SuppliersSearchComponent, HxBankAccountSelectComponent, NgFor, HxInvoiceSelectComponent, NgbTooltip, TranslocoPipe, HxCurrencyPipe]
})
export class PaymentApprovalEditComponent implements OnInit {
  @ViewChild('templateRef') templateRef: TemplateRef<any>;
  @ViewChild('removeTemplateRef') removeTemplateRef: TemplateRef<any>;
  @Output() paymentApprovalSave = new EventEmitter<boolean>();
  isLoading = {
    form: false,
  };
  isEdit = false;
  invoiceFormKeys = {
    id: 'id',
    bankAccountId: 'bankAccountId',
    note: 'note',
    date: 'date',
    supplierId: 'supplierId',
    storeId: 'storeId',
    paymentDestinationCode: 'paymentDestinationCode',
    invoicePayments: 'invoicePayments',
    total: 'total'
  };
  paymentApprovalFormKeys = {
    invoiceId: 'invoiceId',
    value: 'value',
  };
  paymentApprovalForm!: UntypedFormGroup;
  storageInvoices: StorageInvoiceInfoResponseDto[] = [];
  paymentApprovalId: number;
  invoicePaymentId: number;
  currentModal: NgbModalRef;

  @Input()
  set id(id) {
    this._id = id || null;
    if (id) {
      this.getPaymentApprovalById();
    }
  }

  get id() {
    return this._id;
  }

  private _id: number;

  constructor(
    private aRoute: ActivatedRoute,
    private paymentApprovalService: HxPaymentApprovalService,
    private errorService: ErrorHandlerService,
    private router: Router,
    private toastr: HcToastrService,
    private fb: UntypedFormBuilder,
    public modal: NgbModal,
    private tr: TranslocoService
  ) {
  }

  ngOnInit(): void {
    this.createForm();
  }

  asFormArray(path: string): UntypedFormArray {
    return this.paymentApprovalForm.get(path) as UntypedFormArray;
  }

  async onSubmitForm(modal) {
    const obj: PaymentApprovalRequest = this.paymentApprovalForm.getRawValue();
    try {
      await this.paymentApprovalService.savePaymentApproval(obj);
      this.toastr.success(this.tr.translate('payment-approval-edit.ts.save'));
      this.paymentApprovalSave.emit(true);
      modal.dismiss();
    } catch (err: any) {
      this.errorService.check(err?.error);
    }
  }

  addItem() {
    const items = this.asFormArray(this.invoiceFormKeys.invoicePayments);
    items.push(this.createPaymentApprovalItem());
  }

  removeItem(index: number) {
    const items = this.asFormArray(this.invoiceFormKeys.invoicePayments);
    if (items.at(index).value?.id) {
      this.invoicePaymentId = items.at(index).value?.id;
      this.currentModal = this.modal.open(this.removeTemplateRef, { size: 'sm', centered: true });
      this.currentModal.result.then(() => {
        const currentTotal = this.paymentApprovalForm.controls['total'].value;
        this.paymentApprovalForm.controls['total'].patchValue(currentTotal - (items.at(index).value?.value ?? 0));
        items.removeAt(index);
        this.invoicePaymentId = undefined;
      }, err => {
        this.invoicePaymentId = undefined;
        this.errorService.handler(err.error);
      });
    } else {
      items.removeAt(index);
      this.storageInvoices.splice(index, 1);
    }
  }

  onInvoiceChanged(inv: StorageInvoiceModel | StorageInvoiceModel[] | undefined, index: number, item: AbstractControl) {
    if (inv && !Array.isArray(inv)) {
      this.storageInvoices[index] = inv;
      this.storageInvoices[index].debtTotal = this.getDebtTotal(inv);
    } else {
      this.storageInvoices.splice(index, 1);
    }
    (item as UntypedFormGroup).controls[this.paymentApprovalFormKeys.value].setValue(null);
  }

  onSupplierChaged() {
    this.clearInvoicePayments();
    this.storageInvoices = [];
  }

  closeModal(modal) {
    this.paymentApprovalSave.emit(false);
    this.clearInvoicePayments();
    this.paymentApprovalForm.reset();
    this.storageInvoices = [];

    if (modal) {
      modal.dismiss();
    }
  }

  showModal() {
    this.isEdit = false;
    this.addPaymentApproval();
  }

  onValueChanged() {
    const items = this.asFormArray(this.invoiceFormKeys.invoicePayments);
    let totalValue = 0;
    for (let c of items.controls) {
      const val = (c.value?.value) ? Number(c.value.value) : 0;
      totalValue = totalValue + val;
    }
    this.paymentApprovalForm.controls['total'].patchValue(totalValue);
  }

  async removeConfirm(modal) {
    try {
      await this.paymentApprovalService.removePaymentApprovalInvoice(this.paymentApprovalId, this.invoicePaymentId);
      this.currentModal.close();
    } catch (err: any) {
      modal.dismiss();
      this.errorService.handler(err?.error);
    }
  }

  private createForm(): void {
    if (!this.paymentApprovalForm) {
      this.paymentApprovalForm = this.fb.group({
        id: [''],
        bankAccountId: ['', [Validators.required]],
        date: [isoDate(Date.now())],
        supplierId: ['', [Validators.required]],
        storeId: extractDefaultStoreId(),
        invoicePayments: this.fb.array([]),
        paymentDestinationCode: ['', [Validators.required]],
        note: [''],
        total: ['', [Validators.required]],
      });
    }
  }

  private createPaymentApprovalItem() {
    return this.fb.group({
      invoiceId: [null],
      value: [null],
    });
  }

  private async getPaymentApprovalById() {
    this.isLoading.form = true;
    setTimeout(() => {
      this.addPaymentApproval();
    }, 0);
    try {
      const result = await this.paymentApprovalService.getPaymentApprovalById(this.id);
      if (result) {
        this.paymentApprovalId = result.id;
        this.updateForm(result);
      }
    } catch (err: any) {
      this.errorService.handler(err?.error);
    } finally {
      this.isLoading.form = false;
    }
  }

  private updateForm(item: PaymentApprovalModel) {
    this.isEdit = true;
    this.paymentApprovalForm.patchValue(item);
    this.paymentApprovalForm.controls['bankAccountId'].patchValue(item.bankAccount?.id);
    this.paymentApprovalForm.controls['supplierId'].patchValue(item.supplier?.id);

    const items = this.asFormArray(this.invoiceFormKeys.invoicePayments);
    let totalValue = 0;
    if (item.invoicePayments && item.invoicePayments.length) {
      item.invoicePayments.forEach(ip => {
        const total = this.getDebtTotal(ip.invoice);
        if (total > 0) {
          items.push(this.fb.group({id: ip.id, invoiceId: ip.invoice.id, value: ip.value}));
          totalValue = totalValue + ip.value;
        }
      });
    } else {
      totalValue = item.total;
    }

    this.paymentApprovalForm.controls['total'].patchValue(totalValue);
  }

  private addPaymentApproval() {
    const modalRef = this.modal.open(this.templateRef, {size: 'lg'});
    modalRef.result.then(result => {
      this.closeModal(result);
    }, err => {
      this.closeModal(err);
    });
  }

  private clearInvoicePayments() {
    const items = this.asFormArray(this.invoiceFormKeys.invoicePayments);
    items.clear();
  }

  private getDebtTotal(inv: StorageInvoiceModel): number {
    let total = (inv.totalPrice ?? 0) - (inv.paidTotal ?? 0);
    if (total < 0) {
      total = 0;
    }
    return total;
  }
}
